import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { carregarNotificacoes, Notificacao } from './notificacoes';
import './style.css';

/**
 * Central de notificações para o header (menu horizontal): sino com contador e
 * dropdown listando cadastros pendentes de informação. Clicar abre o cadastro.
 */
const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const [itens, setItens] = useState<Notificacao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const carregar = () => {
    setCarregando(true);
    carregarNotificacoes()
      .then(setItens)
      .catch(() => setItens([])) // silencioso: notificações não devem quebrar o header
      .finally(() => setCarregando(false));
  };

  useEffect(() => { carregar(); }, []);

  // painel abre ABAIXO do sino, alinhado à direita (header fica no topo-direito)
  useLayoutEffect(() => {
    if (!aberto) return;
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const m = 8;
    const w = panelRef.current?.offsetWidth ?? 300;
    const left = Math.max(m, Math.min(r.right - w, window.innerWidth - w - m));
    setPos({ top: r.bottom + 6, left });
  }, [aberto, itens, carregando]);

  // fecha ao clicar fora / redimensionar
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setAberto(false);
    };
    const fechar = () => setAberto(false);
    document.addEventListener('mousedown', fora);
    window.addEventListener('resize', fechar);
    return () => {
      document.removeEventListener('mousedown', fora);
      window.removeEventListener('resize', fechar);
    };
  }, [aberto]);

  const total = itens.length;

  const abrir = () => {
    if (!aberto) carregar(); // atualiza ao abrir
    setAberto((o) => !o);
  };

  const irPara = (n: Notificacao) => {
    setAberto(false);
    navigate(n.href);
  };

  return (
    <div className="nc-wrap" ref={wrapRef}>
      <button
        ref={triggerRef}
        className="nc-trigger"
        onClick={abrir}
        title={`Notificações${total ? ` (${total})` : ''}`}
        aria-haspopup="menu"
        aria-expanded={aberto}
      >
        <Bell size={20} />
        {total > 0 && <span className="nc-badge">{total > 99 ? '99+' : total}</span>}
      </button>

      {aberto && createPortal(
        <div className="nc-panel" role="menu" ref={panelRef} style={{ top: pos.top, left: pos.left }}>
          <div className="nc-panel-head">
            <strong>Notificações</strong>
            <button className="nc-close" onClick={() => setAberto(false)} aria-label="Fechar"><X size={15} /></button>
          </div>
          <div className="nc-panel-body">
            {carregando ? (
              <div className="nc-empty">Carregando…</div>
            ) : total === 0 ? (
              <div className="nc-empty">Nenhuma pendência. 🎉</div>
            ) : (
              <ul className="nc-list">
                {itens.map((n) => (
                  <li key={n.id} className="nc-item" onClick={() => irPara(n)} role="menuitem">
                    <span className="nc-dot" />
                    <div className="nc-item-text">
                      <span className="nc-item-titulo">{n.titulo}</span>
                      <span className="nc-item-desc">{n.descricao}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default NotificationCenter;
